import React from 'react';
import { NavigateFunction } from 'react-router-dom';
import { CellContext } from '@tanstack/react-table';
import cn from 'classnames';
import { utcToZonedTime } from 'date-fns-tz';

import { AdaptiveTableCell, IconCell } from '@components/common';
import Actions from '@components/common/Actions';

import { useAppDispatch } from '@store/store';

import { viewOnMap } from '@slices/map';

import { formatDate } from '@utils/kpi-utils/date';

import { formatTemplateDefault, timezoneUTC } from '@constants/common';
import { routes } from '@constants/routes';

import styles from './AssetCartRequestFullView.module.scss';

const { map } = routes;

interface IGetColumns {
	navigate: NavigateFunction;
	openEditStartEndDateForm: (assetId: string, assetDbId: string) => void;
	setSingleIdForRemove: React.Dispatch<React.SetStateAction<string | null>>;
	openDeleteConfirm: () => void;
}

export const getColumns = ({
	navigate,
	openEditStartEndDateForm,
	setSingleIdForRemove,
	openDeleteConfirm,
}: IGetColumns) => ([
	{
		header: 'Asset ID',
		accessorKey: 'assetId',
		cell: ({ getValue }: CellContext<any, any>) => {
			const assetId = getValue();

			return <AdaptiveTableCell text={assetId} />;
		},
	},
	{
		header: 'Asset Owner',
		accessorKey: 'assetOwnerName',
		cell: ({ getValue }: CellContext<any, any>) => {
			const assetOwnerName = getValue();

			return <AdaptiveTableCell text={assetOwnerName} />;
		},
	},
	{
		header: 'Asset Type',
		accessorKey: 'assetType',
		cell: ({ getValue }: CellContext<any, any>) => {
			const assetType = getValue();

			return (
				<IconCell
					id={assetType?.icon?.id}
					text={assetType?.assetTypeName}
				/>
			);
		},
	},
	{
		header: 'Asset Subtype',
		accessorKey: 'assetSubType',
		cell: ({ getValue }: CellContext<any, any>) => {
			const assetSubType = getValue();

			return <AdaptiveTableCell text={assetSubType?.assetSubTypeName || 'n/a'} />;
		},
	},
	{
		header: 'Start Date',
		accessorKey: 'startRentDate',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const startRentDate = getValue();
			const date = formatDate(utcToZonedTime(startRentDate, timezoneUTC), formatTemplateDefault);

			return <AdaptiveTableCell text={date} />;
		},
	},
	{
		header: 'End Date',
		accessorKey: 'endRentDate',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const endRentDate = getValue();
			const date = formatDate(utcToZonedTime(endRentDate, timezoneUTC), formatTemplateDefault);

			return <AdaptiveTableCell text={date} />;
		},
	},
	{
		header: 'Period Availability',
		accessorKey: 'isAvailableInPeriod',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const isAvailableInPeriod = getValue();

			return (
				<AdaptiveTableCell
					className={cn({
						[styles.periodAvailabilityTextUnavailable]: !isAvailableInPeriod,
					})}
					text={isAvailableInPeriod ? 'Available' : 'Unavailable'}
				/>
			);
		},
	},
	{
		header: 'Price',
		accessorKey: 'price',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const price = getValue();

			return <AdaptiveTableCell text={price ? `£${price}` : ''} />;
		},
	},
	{
		header: () => (
			<div className={styles.centerColumnHeader}>
				Actions
			</div>
		),
		accessorKey: 'actions',
		enableSorting: false,
		cell: function Cell({ row }: CellContext<any, any>) {
			const dispatch = useAppDispatch();

			const handleDelete = () => {
				setSingleIdForRemove(row?.original?.id);
				openDeleteConfirm();
			};

			const handleViewOnMap = () => {
				void dispatch(viewOnMap({ assetId: row?.original?.assetDbId }));

				navigate(map.index, { state: { isRedirectedFromAdminPanel: true } });
			};

			const editDate = () => openEditStartEndDateForm(row?.original?.id, row?.original?.assetDbId);

			return (
				<Actions
					deleteTooltip='Remove from the list'
					editDateHandler={editDate}
					handleDelete={handleDelete}
					isDelete
					isEditDate
					isViewOnMap
					viewOnMapHandler={handleViewOnMap}
					viewOnMapTooltip='View on the map'
				/>
			);
		},
	},
]);
